import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import authService, {
    LoginRequest,
    RegisterRequest,
    SimpleRegisterRequest,
    ProfileCompletionRequest,
    UpdateProfileRequest,
    UpdateUserProfileRequest,
    ChangePasswordRequest,
    ForceChangePasswordRequest,
    UpdateUserRequest,
} from '@/services/authService';
import {
    loginRequest,
    loginSuccess,
    loginFailure,
    registerRequest,
    registerSuccess,
    registerFailure,
    fullRegisterRequest,
    fullRegisterSuccess,
    fullRegisterFailure,
    completeProfileRequest,
    completeProfileSuccess,
    completeProfileFailure,
    checkProfileCompletionRequest,
    checkProfileCompletionSuccess,
    checkProfileCompletionFailure,
    logoutRequest,
    logoutSuccess,
    logoutFailure,
    getCurrentUserRequest,
    getCurrentUserSuccess,
    getCurrentUserFailure,
    updateUserRequest,
    updateUserSuccess,
    updateUserFailure,
    updateProfileRequest,
    updateProfileSuccess,
    updateProfileFailure,
    fetchUserProfileRequest,
    fetchUserProfileSuccess,
    fetchUserProfileFailure,
    updateUserProfileRequest,
    updateUserProfileSuccess,
    updateUserProfileFailure,
    changePasswordRequest,
    changePasswordSuccess,
    changePasswordFailure,
    forceChangePasswordRequest,
    forceChangePasswordSuccess,
    forceChangePasswordFailure,
    fetchUserStatsSuccess,
    fetchUserStatsFailure,
    fetchUserPreferencesRequest,
    fetchUserPreferencesSuccess,
    fetchUserPreferencesFailure,
    updateUserPreferencesRequest,
    updateUserPreferencesSuccess,
    updateUserPreferencesFailure,
    startLearningSessionRequest,
    startLearningSessionSuccess,
    startLearningSessionFailure,
    endLearningSessionRequest,
    endLearningSessionSuccess,
    endLearningSessionFailure,
    forgotPasswordRequest,
    forgotPasswordSuccess,
    forgotPasswordFailure,
    resetPasswordRequest,
    resetPasswordSuccess,
    resetPasswordFailure,
    initializeAuthFromToken,
    refreshTokenRequest,
    refreshTokenSuccess,
    refreshTokenFailure,
    authInitDone,
} from '../slices/authSlice';
import { AppEvent, emitter } from '@/plugin/mitt';
import { closeModal } from '../slices/modalSlice';
import { MODAL, SEVERITY_NOTIFICATION } from '@/common/constants';
import { t } from 'i18next';
import { first, get } from 'lodash';

// Worker Sagas
function* loginWorker(action: PayloadAction<LoginRequest>): SagaIterator {
    try {
        const response = yield call(authService.login, action.payload);
        // The API set the HttpOnly auth cookies; only remember that a session exists
        authService.markSignedIn();
        yield put(loginSuccess(response));
        yield call(authService.initializePushNotifications);
        if (response.must_change_password) {
            emitter.emit(AppEvent.NAVIGATE, { to: '/change-password-required' });
        } else {
            emitter.emit(AppEvent.NAVIGATE, { to: '/student/dashboard' });
        }
    } catch (error: any) {
        if (error.response?.data) {
            const code = `common.${first(get(error, "response.data.code"))}` || `common.ERROR_LOGIN_FAILED`
            const message = `${first(get(error, "response.data.message"))}` || t(`common.ERROR_LOGIN_FAILED`)

            yield put(loginFailure(message));

            emitter.emit(AppEvent.SHOW_NOTIFICATION, {
                code,
                message,
                severity: SEVERITY_NOTIFICATION.ERROR,
            });
        } else {
            yield put(loginFailure(error.response?.data?.message || t(`common.ERROR_LOGIN_FAILED`)));
            emitter.emit(AppEvent.SHOW_NOTIFICATION, {
                message: error.response?.data?.message || t(`common.ERROR_LOGIN_FAILED`),
                severity: SEVERITY_NOTIFICATION.ERROR,
            });
        }
    }
}

function* registerWorker(action: PayloadAction<SimpleRegisterRequest>): SagaIterator {
    try {
        const response = yield call(authService.simpleRegister, action.payload);
        yield put(registerSuccess(response));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: t('auth.register.successMessage'),
            severity: SEVERITY_NOTIFICATION.SUCCESS,
        });
        emitter.emit(AppEvent.NAVIGATE, { to: '/auth/login' });
    } catch (error: any) {
        // Handle validation errors that might be arrays
        if (error.response?.data) {
            const code = `${get(error, "response.data.code")}` || `common.ERROR_REGISTER_FAILED`
            const message = `${get(error, "response.data.message")}` || t(`common.ERROR_REGISTER_FAILED`)

            yield put(registerFailure(code));
            emitter.emit(AppEvent.SHOW_NOTIFICATION, {
                code: `common.${code}`,
                message,
                severity: SEVERITY_NOTIFICATION.ERROR,
            });
        } else {
            yield put(registerFailure(error.response?.data?.message || t(`common.ERROR_REGISTER_FAILED`)));
            emitter.emit(AppEvent.SHOW_NOTIFICATION, {
                message: error.response?.data?.message || t(`common.ERROR_REGISTER_FAILED`),
                severity: SEVERITY_NOTIFICATION.ERROR,
            });
        }
    }
}

function* fullRegisterWorker(action: PayloadAction<RegisterRequest>): SagaIterator {
    try {
        const response = yield call(authService.register, action.payload);
        yield put(fullRegisterSuccess(response));
    } catch (error: any) {
        yield put(fullRegisterFailure(error.response?.data?.message || t(`common.ERROR_REGISTER_FAILED`)));
    }
}

function* completeProfileWorker(action: PayloadAction<ProfileCompletionRequest>): SagaIterator {
    try {
        const response = yield call(authService.completeProfile, action.payload);
        yield put(completeProfileSuccess(response));
        yield put(closeModal(MODAL.SURVEY));
        emitter.emit(AppEvent.NAVIGATE, { to: '/testing' });
    } catch (error: any) {
        const data = error.response?.data;
        yield put(
            completeProfileFailure(data?.phone_number?.code || data?.message || 'Profile completion failed'),
        );
    }
}

function* checkProfileCompletionWorker(): SagaIterator {
    try {
        const response = yield call(authService.checkProfileCompletion);
        yield put(checkProfileCompletionSuccess(response));
    } catch (error: any) {
        yield put(checkProfileCompletionFailure(error.response?.data?.message || 'Failed to check profile completion'));
    }
}

function* logoutWorker(): SagaIterator {
    try {
        // The API blacklists the refresh cookie and clears the auth cookies
        yield call([authService, authService.logout]);
        yield put(logoutSuccess());
    } catch (error: any) {
        // Even if logout fails on server, clear local storage
        authService.clearSession();
        yield put(logoutFailure(error.response?.data?.message || 'Logout failed'));
    }
}

function* getCurrentUserWorker(): SagaIterator {
    try {
        const user = yield call(authService.getCurrentUser);
        yield put(getCurrentUserSuccess(user));
    } catch (error: any) {
        yield put(getCurrentUserFailure(error.response?.data?.message || 'Failed to get user'));
    }
}

function* updateUserWorker(action: PayloadAction<UpdateUserRequest>): SagaIterator {
    try {
        const user = yield call(authService.updateUser, action.payload);
        yield put(updateUserSuccess(user));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: 'User updated successfully',
            severity: SEVERITY_NOTIFICATION.SUCCESS,
        });
    } catch (error: any) {
        yield put(updateUserFailure(error.response?.data?.message || 'Failed to update user'));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: error.response?.data?.message || 'Failed to update user',
            severity: SEVERITY_NOTIFICATION.ERROR,
        });
    }
}
function* updateProfileWorker(action: PayloadAction<UpdateProfileRequest>): SagaIterator {
    try {
        yield call(authService.updateProfile, action.payload);
        const user = yield call(authService.getCurrentUser);
        yield put(getCurrentUserSuccess(user));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: 'Profile updated successfully',
            severity: SEVERITY_NOTIFICATION.SUCCESS,
        });
    } catch (error: any) {
        yield put(updateProfileFailure(error.response?.data?.message || 'Profile update failed'));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: error.response?.data?.message || 'Profile update failed',
            severity: SEVERITY_NOTIFICATION.ERROR,
        });
    }
}

function* forceChangePasswordWorker(action: PayloadAction<ForceChangePasswordRequest>): SagaIterator {
    try {
        yield call(authService.forceChangePassword, action.payload);
        yield put(forceChangePasswordSuccess());
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: t('auth.forceChangePassword.success'),
            severity: SEVERITY_NOTIFICATION.SUCCESS,
        });
        emitter.emit(AppEvent.NAVIGATE, { to: '/student/dashboard' });
    } catch (error: any) {
        const message =
            error.response?.data?.message ||
            error.response?.data?.errors?.old_password?.[0] ||
            error.response?.data?.errors?.new_password?.[0] ||
            'Password change failed';
        yield put(forceChangePasswordFailure(message));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message,
            severity: SEVERITY_NOTIFICATION.ERROR,
        });
    }
}

function* changePasswordWorker(action: PayloadAction<ChangePasswordRequest>): SagaIterator {
    try {
        const response = yield call(authService.changePassword, action.payload);
        yield put(changePasswordSuccess(response.message || 'Password changed successfully'));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: response.message || 'Password changed successfully',
            severity: SEVERITY_NOTIFICATION.SUCCESS,
        });
    } catch (error: any) {
        // Handle field-specific errors
        if (error.response?.data) {
            const serverErrors = error.response.data;

            // If there are field-specific errors, format them with friendly names
            if (typeof serverErrors === 'object') {
                // If we have field-specific errors, pass them as an object
                if (Object.keys(serverErrors).length > 0) {
                    yield put(changePasswordFailure(JSON.stringify(serverErrors)));
                    emitter.emit(AppEvent.SHOW_NOTIFICATION, {
                        message: 'Password change failed',
                        severity: SEVERITY_NOTIFICATION.ERROR,
                    });
                } else {
                    yield put(changePasswordFailure('Password change failed'));
                    emitter.emit(AppEvent.SHOW_NOTIFICATION, {
                        message: 'Password change failed',
                        severity: SEVERITY_NOTIFICATION.ERROR,
                    });
                }
            } else {
                yield put(changePasswordFailure('Password change failed'));
                emitter.emit(AppEvent.SHOW_NOTIFICATION, {
                    message: 'Password change failed',
                    severity: SEVERITY_NOTIFICATION.ERROR,
                });
            }
        } else {
            yield put(changePasswordFailure(error.message || 'Password change failed'));
            emitter.emit(AppEvent.SHOW_NOTIFICATION, {
                message: 'Password change failed',
                severity: SEVERITY_NOTIFICATION.ERROR,
            });
        }
    }
}

function* fetchUserProfileWorker(): SagaIterator {
    try {
        const profile = yield call(authService.getUserProfile);
        yield put(fetchUserProfileSuccess(profile));
    } catch (error: any) {
        yield put(fetchUserProfileFailure(error.response?.data?.message || 'Failed to fetch profile'));
    }
}

function* updateUserProfileWorker(action: PayloadAction<UpdateUserProfileRequest>): SagaIterator {
    try {
        //@ts-ignore
        const profile = yield call(authService.updateUserProfile, { userData: action.payload });
        yield put(updateUserProfileSuccess(profile));
    } catch (error: any) {
        yield put(updateUserProfileFailure(error.response?.data?.message || 'Failed to update profile'));
    }
}

function* fetchUserPreferencesWorker(): SagaIterator {
    try {
        const preferences = yield call(authService.getUserPreferences);
        yield put(fetchUserPreferencesSuccess(preferences));
    } catch (error: any) {
        yield put(fetchUserPreferencesFailure(error.response?.data?.message || 'Failed to fetch preferences'));
    }
}

function* updateUserPreferencesWorker(action: PayloadAction<any>): SagaIterator {
    try {
        yield call(authService.updatePreferences, action.payload);
        yield put(updateUserPreferencesSuccess(action.payload));
    } catch (error: any) {
        yield put(updateUserPreferencesFailure(error.response?.data?.message || 'Failed to update preferences'));
    }
}

function* startLearningSessionWorker(action: PayloadAction<any>): SagaIterator {
    try {
        const session = yield call(authService.startSession, action.payload);
        yield put(startLearningSessionSuccess(session));
    } catch (error: any) {
        yield put(startLearningSessionFailure(error.response?.data?.message || 'Failed to start session'));
    }
}

function* endLearningSessionWorker(action: PayloadAction<any>): SagaIterator {
    try {
        const session = yield call(authService.endSession, action.payload);
        yield put(endLearningSessionSuccess(session));
    } catch (error: any) {
        yield put(endLearningSessionFailure(error.response?.data?.message || 'Failed to end session'));
    }
}

function* forgotPasswordWorker(action: PayloadAction<string>): SagaIterator {
    try {
        yield call(authService.resetPassword, action.payload);
        yield put(forgotPasswordSuccess());
    } catch (error: any) {
        yield put(forgotPasswordFailure(error.response?.data?.message || 'Forgot password failed'));
    }
}

function* resetPasswordWorker(action: PayloadAction<{ token: string; newPassword: string }>): SagaIterator {
    try {
        const resetData = {
            token: action.payload.token,
            new_password: action.payload.newPassword,
            new_password_confirm: action.payload.newPassword,
        };
        yield call(authService.confirmResetPassword, resetData);
        yield put(resetPasswordSuccess());
    } catch (error: any) {
        yield put(resetPasswordFailure(error.response?.data?.message || 'Password reset failed'));
    }
}

function* initializeAuthFromTokenWorker(): SagaIterator {
    try {
        // Older builds kept the tokens in localStorage: move that session to cookies first
        yield call([authService, authService.migrateLegacyTokens]);
        if (!authService.hasSession()) {
            yield put(authInitDone());
            return;
        }
        // Signed in only if /auth/user/ accepts the cookie session
        const user = yield call(authService.getCurrentUser);
        yield put(getCurrentUserSuccess(user));

        // Also fetch user profile
        try {
            const profile = yield call(authService.getUserProfile);
            yield put(fetchUserProfileSuccess(profile));
        } catch (profileError) {
            // Profile fetch is not critical, just log the error
            console.warn('Failed to fetch user profile:', profileError);
        }
    } catch (error: any) {
        // Session is invalid, forget it
        authService.clearSession();
        yield put(getCurrentUserFailure(error.response?.data?.message || 'Token validation failed'));
    }
}

function* refreshTokenWorker(): SagaIterator {
    try {
        yield call([authService, authService.refreshToken]);
        yield put(refreshTokenSuccess());
    } catch (error: any) {
        yield put(refreshTokenFailure(error.response?.data?.message || 'Token refresh failed'));
    }
}

// Watcher Sagas
function* watchAuth(): SagaIterator {
    yield takeLatest(loginRequest.type, loginWorker);
    yield takeLatest(registerRequest.type, registerWorker);
    yield takeLatest(fullRegisterRequest.type, fullRegisterWorker);
    yield takeLatest(completeProfileRequest.type, completeProfileWorker);
    yield takeLatest(checkProfileCompletionRequest.type, checkProfileCompletionWorker);
    yield takeLatest(logoutRequest.type, logoutWorker);
    yield takeLatest(getCurrentUserRequest.type, getCurrentUserWorker);
    yield takeLatest(updateUserRequest.type, updateUserWorker);
    yield takeLatest(updateProfileRequest.type, updateProfileWorker);
    yield takeLatest(fetchUserProfileRequest.type, fetchUserProfileWorker);
    yield takeLatest(updateUserProfileRequest.type, updateUserProfileWorker);
    yield takeLatest(changePasswordRequest.type, changePasswordWorker);
    yield takeLatest(forceChangePasswordRequest.type, forceChangePasswordWorker);
    yield takeLatest(fetchUserPreferencesRequest.type, fetchUserPreferencesWorker);
    yield takeLatest(updateUserPreferencesRequest.type, updateUserPreferencesWorker);
    yield takeLatest(startLearningSessionRequest.type, startLearningSessionWorker);
    yield takeLatest(endLearningSessionRequest.type, endLearningSessionWorker);
    yield takeLatest(forgotPasswordRequest.type, forgotPasswordWorker);
    yield takeLatest(resetPasswordRequest.type, resetPasswordWorker);
    yield takeLatest(initializeAuthFromToken.type, initializeAuthFromTokenWorker);
    yield takeLatest(refreshTokenRequest.type, refreshTokenWorker);
}

export default watchAuth;
